import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import ProductCard from "@/components/ProductCard";
import CTASection from "@/components/CTASection";
import { products as staticProducts, CATEGORIES, SENSORS, APPLICATIONS, POWER } from "@/data/products";
import { useCatalog } from "@/lib/catalog";
import { track } from "@/lib/api";
import { SlidersHorizontal, X } from "lucide-react";

const FilterGroup = ({ title, options, selected, onToggle, testId }) => (
  <div className="mb-7">
    <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-3">{title}</p>
    <div className="flex flex-wrap gap-2 lg:flex-col lg:gap-1.5">
      {options.map((o) => {
        const active = selected.includes(o);
        return (
          <button
            key={o}
            onClick={() => onToggle(o)}
            data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            aria-pressed={active}
            className={`text-left text-sm px-3 py-1.5 border transition-colors ${
              active ? "border-signal bg-signal text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-400 bg-white"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  </div>
);

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ category: [], sensor: [], application: [], power: [] });
  const [mobileOpen, setMobileOpen] = useState(false);
  const { products } = useCatalog();

  useEffect(() => {
    const cat = params.get("category");
    if (cat && CATEGORIES.includes(cat)) setFilters((f) => ({ ...f, category: [cat] }));
  }, [params]);

  const toggle = (group, value) => {
    setFilters((f) => {
      const cur = f[group];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...f, [group]: next };
    });
    track("product_filter", { group, value });
  };

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (filters.category.length && !filters.category.includes(p.category)) return false;
      if (filters.sensor.length && (!p.sensor || !filters.sensor.includes(p.sensor))) return false;
      if (filters.application.length && !filters.application.some((a) => p.applications.includes(a))) return false;
      if (filters.power.length && !filters.power.includes(p.power)) return false;
      return true;
    });
  }, [filters, products]);

  const activeCount = Object.values(filters).flat().length;

  const panel = (
    <>
      <FilterGroup title="Category" options={CATEGORIES} selected={filters.category} onToggle={(v) => toggle("category", v)} testId="filter-category" />
      <FilterGroup title="Sensor" options={SENSORS} selected={filters.sensor} onToggle={(v) => toggle("sensor", v)} testId="filter-sensor" />
      <FilterGroup title="Application" options={APPLICATIONS} selected={filters.application} onToggle={(v) => toggle("application", v)} testId="filter-application" />
      <FilterGroup title="Power" options={POWER} selected={filters.power} onToggle={(v) => toggle("power", v)} testId="filter-power" />
      {activeCount > 0 && (
        <button
          onClick={() => { setFilters({ category: [], sensor: [], application: [], power: [] }); setParams({}); }}
          className="text-xs font-medium text-signal flex items-center gap-1"
          data-testid="clear-filters-btn"
        >
          <X size={13} /> Clear all filters
        </button>
      )}
    </>
  );

  return (
    <div className="bg-bone min-h-screen" data-testid="products-page">
      <SEO
        title="Products — Bluetooth Mesh Lighting Controls"
        description="Browse the complete Amaya Pro catalog: indoor and outdoor BLE controls, nodes, power pack nodes, wall switches, wall outlet and long range extenders."
        path="/products"
      />
      <PageHero
        overline="Product Catalog"
        title="The Amaya Pro ecosystem."
        sub="Thirty control devices across eight categories — every one connected through Bluetooth Mesh, every one backed by a 10-year in-house warranty."
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <button
          className="lg:hidden mb-6 flex items-center gap-2 px-4 py-2.5 border border-zinc-300 bg-white text-sm font-medium"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-filter-btn"
          aria-expanded={mobileOpen}
        >
          <SlidersHorizontal size={15} /> Filters {activeCount > 0 && `(${activeCount})`}
        </button>
        {mobileOpen && <div className="lg:hidden mb-8 p-5 bg-white border border-zinc-200" data-testid="mobile-filter-panel">{panel}</div>}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
          <aside className="hidden lg:block" aria-label="Product filters">
            <div className="sticky top-24 p-5 bg-white border border-zinc-200" data-testid="filter-panel">{panel}</div>
          </aside>
          <div>
            <p className="font-mono text-xs text-zinc-500 mb-6" data-testid="result-count">
              {filtered.length} of {products.length} products
            </p>
            {filtered.length === 0 ? (
              <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500" data-testid="no-results">
                No products match these filters. Try clearing a filter.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <CTASection title="Need help choosing a product?" sub="Tell us about your project and we'll recommend the right controls." />
    </div>
  );
}
